import { useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";
import { widgetMenuOptionsAtom, widgetMenuOptionsByNameAtom } from "@/interface/AppStore";
import { useHotkeys } from "@/interface/hooks";
import { applyValue, setNewOptions } from "../../lib";
import type { ActiveSubMenuState, SetActiveSubMenu } from "../../types";
import { SubMenu } from "../SubMenu/SubMenu";
import { TextFormatMenuContent } from "./TextFormatMenuContent";

interface TextFormatMenuProps {
    activeSubMenu: ActiveSubMenuState;
    setActiveSubMenu: SetActiveSubMenu;
}

export function TextFormatMenu({ activeSubMenu, setActiveSubMenu }: TextFormatMenuProps) {
    const textBoldOptions = useAtomValue(widgetMenuOptionsByNameAtom("textBold"));
    const textStrikeOptions = useAtomValue(widgetMenuOptionsByNameAtom("textStrike"));
    const setWidgetMenuOptions = useSetAtom(widgetMenuOptionsAtom);

    const textBoldUniqueValues = [...new Set(textBoldOptions.map((option) => option.value))];
    const textStrikeUniqueValues = [...new Set(textStrikeOptions.map((option) => option.value))];
    const isStriked = textStrikeUniqueValues.length === 1 && (textStrikeUniqueValues[0] as boolean);
    const isBold = textBoldUniqueValues.length === 1 && textBoldUniqueValues[0] === "bold";

    const handleTextBoldClick = useCallback(() => {
        const setTextBoldOptions = setNewOptions({ name: "textBold", setWidgetMenuOptions });

        function handleClose() {
            setActiveSubMenu(null);
        }

        applyValue({
            options: textBoldOptions,
            value: isBold ? "" : "bold",
            setNewOptions: setTextBoldOptions,
            handleClose,
        });
    }, [textBoldOptions, isBold, setWidgetMenuOptions, setActiveSubMenu]);

    const handleTextStrikeClick = useCallback(() => {
        function handleClose() {
            setActiveSubMenu(null);
        }

        const setTextStrikeOptions = setNewOptions({
            name: "textStrike",
            setWidgetMenuOptions,
        });

        applyValue({
            options: textStrikeOptions,
            value: !isStriked,
            setNewOptions: setTextStrikeOptions,
            handleClose,
        });
    }, [textStrikeOptions, isStriked, setWidgetMenuOptions, setActiveSubMenu]);

    useHotkeys("cmd+b, ctrl+b", handleTextBoldClick);

    return (
        <SubMenu activeSubMenu={activeSubMenu}>
            <TextFormatMenuContent
                onTextBoldButtonClick={handleTextBoldClick}
                onTextStrikeButtonClick={handleTextStrikeClick}
            />
        </SubMenu>
    );
}
